import { useState, useEffect } from 'react';
import { Plus, Shield, Trash2, Edit, Lock, Users as UsersIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import api from '@/lib/api';
import { toast } from 'sonner';

// Labels amigáveis
const MODULO_LABEL = {
  dashboard: 'Dashboard',
  clientes: 'Clientes',
  veiculos: 'Veículos',
  orcamentos: 'Orçamentos',
  ordens: 'Ordens de Serviço',
  financeiro: 'Financeiro (Fluxo)',
  custos: 'Custos',
  estoque: 'Estoque',
  contabilidade: 'Contabilidade',
  configuracoes: 'Configurações',
  usuarios: 'Usuários',
  cargos: 'Cargos e Permissões',
};

const ACAO_LABEL = {
  view: 'Visualizar',
  create: 'Criar',
  edit: 'Editar',
  delete: 'Excluir',
  finalizar: 'Finalizar',
};

const CORES = ['#3b82f6', '#f59e0b', '#10b981', '#8b5cf6', '#ec4899', '#64748b', '#ef4444', '#06b6d4'];

function Cargos() {
  const [cargos, setCargos] = useState([]);
  const [catalogo, setCatalogo] = useState({});
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    nome: '',
    descricao: '',
    cor: '#3b82f6',
    ativo: true,
    permissoes: [],
  });

  useEffect(() => {
    Promise.all([loadCargos(), loadCatalogo()]).finally(() => setLoading(false));
  }, []);

  const loadCargos = async () => {
    try {
      const { data } = await api.get('/cargos');
      setCargos(data);
    } catch (e) {
      toast.error('Erro ao carregar cargos');
    }
  };

  const loadCatalogo = async () => {
    try {
      const { data } = await api.get('/cargos/catalogo');
      setCatalogo(data);
    } catch (e) {
      toast.error('Erro ao carregar catálogo de permissões');
    }
  };

  const openCreate = () => {
    setEditing(null);
    setFormData({ nome: '', descricao: '', cor: '#3b82f6', ativo: true, permissoes: [] });
    setIsDialogOpen(true);
  };

  const openEdit = (cargo) => {
    setEditing(cargo);
    setFormData({
      nome: cargo.nome,
      descricao: cargo.descricao || '',
      cor: cargo.cor || '#3b82f6',
      ativo: cargo.ativo !== false,
      permissoes: cargo.permissoes || [],
    });
    setIsDialogOpen(true);
  };

  const togglePerm = (perm) => {
    setFormData((prev) => ({
      ...prev,
      permissoes: prev.permissoes.includes(perm)
        ? prev.permissoes.filter((p) => p !== perm)
        : [...prev.permissoes, perm],
    }));
  };

  const toggleModulo = (modulo) => {
    const acoes = catalogo[modulo] || [];
    const todasPerms = acoes.map((a) => `${modulo}:${a}`);
    setFormData((prev) => {
      const todasSelecionadas = todasPerms.every((p) => prev.permissoes.includes(p));
      return {
        ...prev,
        permissoes: todasSelecionadas
          ? prev.permissoes.filter((p) => !todasPerms.includes(p))
          : [...new Set([...prev.permissoes, ...todasPerms])],
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/cargos/${editing.id}`, formData);
        toast.success('Cargo atualizado');
      } else {
        await api.post('/cargos', formData);
        toast.success('Cargo criado');
      }
      setIsDialogOpen(false);
      loadCargos();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar');
    }
  };

  const handleDelete = async (cargo) => {
    if (cargo.sistema) {
      toast.error('Cargos do sistema não podem ser excluídos');
      return;
    }
    if (!window.confirm(`Excluir o cargo "${cargo.nome}"?`)) return;
    try {
      await api.delete(`/cargos/${cargo.id}`);
      toast.success('Cargo excluído');
      loadCargos();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao excluir');
    }
  };
  if (loading) {
    return <div className="p-8 text-slate-400">Carregando cargos...</div>;
  }

  return (
    <div className="p-4 lg:p-8" data-testid="cargos-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8 gap-4">
        <div>
          <h1 className="text-3xl lg:text-4xl font-bold text-white uppercase tracking-tight mb-2">Cargos & Permissões</h1>
          <p className="text-slate-500 text-sm">Controle granular de acesso por função</p>
        </div>
        <Button onClick={openCreate} className="bg-electric-blue hover:bg-blue-600 text-white rounded-sm" data-testid="add-cargo-btn">
          <Plus className="w-4 h-4 mr-2" />
          Novo Cargo
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {cargos.length === 0 ? (
          <div className="col-span-full bg-carbon border border-white/5 rounded-md p-12 text-center">
            <Shield className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-500">Nenhum cargo cadastrado</p>
          </div>
        ) : (
          cargos.map((cargo) => (
            <div
              key={cargo.id}
              className="bg-carbon border border-white/5 rounded-md p-6 hover:border-white/20 transition-colors"
              data-testid={`cargo-card-${cargo.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: `${cargo.cor}20`, color: cargo.cor }}
                  >
                    <Shield className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                      {cargo.nome}
                      {cargo.sistema && <Lock className="w-3 h-3 text-slate-500" title="Cargo do sistema" />}
                    </h3>
                    <p className="text-xs text-slate-500 line-clamp-1">{cargo.descricao || '—'}</p>
                  </div>
                </div>
                {!cargo.ativo && (
                  <span className="text-xs px-2 py-0.5 rounded bg-red-500/10 text-red-400 uppercase">Inativo</span>
                )}
              </div>

              <div className="flex items-center justify-between text-sm text-slate-400 mb-4">
                <div className="flex items-center gap-2">
                  <UsersIcon className="w-4 h-4" />
                  <span>{cargo.total_usuarios || 0} usuário(s)</span>
                </div>
                <span className="text-electric-blue">{cargo.permissoes?.length || 0} permissões</span>
              </div>

              <div className="flex items-center justify-end space-x-2 pt-4 border-t border-white/5">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => openEdit(cargo)}
                  className="text-electric-blue hover:text-white hover:bg-electric-blue/10"
                  data-testid={`edit-cargo-${cargo.id}`}
                >
                  <Edit className="w-4 h-4 mr-1" />
                  Editar
                </Button>
                {!cargo.sistema && (cargo.total_usuarios || 0) === 0 && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDelete(cargo)}
                    className="text-red-500 hover:text-white hover:bg-red-500/10"
                    data-testid={`delete-cargo-${cargo.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="bg-carbon border-white/10 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold uppercase">
              {editing ? 'Editar Cargo' : 'Novo Cargo'}
              {editing?.sistema && (
                <span className="ml-2 text-xs font-normal text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded normal-case tracking-normal">
                  Cargo do sistema
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-400">Nome do Cargo</Label>
                  <Input
                    value={formData.nome}
                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                    className="bg-obsidian border-white/10 text-white"
                    required
                    data-testid="cargo-nome"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-400">Cor</Label>
                  <div className="flex gap-2 mt-2 flex-wrap">
                    {CORES.map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setFormData({ ...formData, cor: c })}
                        className={`w-8 h-8 rounded-full border-2 transition-all ${
                          formData.cor === c ? 'border-white scale-110' : 'border-transparent'
                        }`}
                        style={{ backgroundColor: c }}
                        data-testid={`cor-${c}`}
                      />
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400">Descrição</Label>
                <Textarea
                  value={formData.descricao}
                  onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                  className="bg-obsidian border-white/10 text-white"
                  rows={2}
                  data-testid="cargo-descricao"
                />
              </div>

              <div className="flex items-center justify-between bg-obsidian/50 p-3 rounded border border-white/5">
                <div>
                  <Label className="text-sm text-white">Cargo Ativo</Label>
                  <p className="text-xs text-slate-500">Usuários com cargo inativo não acessam nada</p>
                </div>
                <Switch
                  checked={formData.ativo}
                  onCheckedChange={(v) => setFormData({ ...formData, ativo: v })}
                  data-testid="cargo-ativo"
                />
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400 mb-3 block">
                  Permissões ({formData.permissoes.length})
                </Label>
                <div className="space-y-3">
                  {Object.keys(catalogo).map((modulo) => {
                    const acoes = catalogo[modulo] || [];
                    const todasPerms = acoes.map((a) => `${modulo}:${a}`);
                    const todasSelecionadas = todasPerms.every((p) => formData.permissoes.includes(p));
                    const algumaSelecionada = todasPerms.some((p) => formData.permissoes.includes(p));
                    return (
                      <div key={modulo} className="bg-obsidian/50 border border-white/5 rounded p-3">
                        <div className="flex items-center justify-between mb-2">
                          <div
                            role="button"
                            tabIndex={0}
                            onClick={() => toggleModulo(modulo)}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleModulo(modulo); } }}
                            className="flex items-center gap-2 text-sm font-semibold text-white hover:text-electric-blue cursor-pointer select-none"
                            data-testid={`modulo-${modulo}`}
                          >
                            <Checkbox
                              checked={todasSelecionadas}
                              data-state={todasSelecionadas ? 'checked' : algumaSelecionada ? 'indeterminate' : 'unchecked'}
                              className="pointer-events-none"
                            />
                            {MODULO_LABEL[modulo] || modulo}
                          </div>
                          <span className="text-xs text-slate-500">
                            {todasPerms.filter((p) => formData.permissoes.includes(p)).length}/{acoes.length}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 pl-6">
                          {acoes.map((acao) => {
                            const perm = `${modulo}:${acao}`;
                            const checked = formData.permissoes.includes(perm);
                            return (
                              <div
                                key={perm}
                                role="button"
                                tabIndex={0}
                                onClick={() => togglePerm(perm)}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); togglePerm(perm); } }}
                                className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer hover:text-white select-none"
                                data-testid={`perm-${perm}`}
                              >
                                <Checkbox
                                  checked={checked}
                                  className="pointer-events-none"
                                />
                                {ACAO_LABEL[acao] || acao}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t border-white/5">
              <Button type="button" variant="ghost" onClick={() => setIsDialogOpen(false)} className="text-slate-400">
                Cancelar
              </Button>
              <Button type="submit" className="bg-electric-blue hover:bg-blue-600 text-white" data-testid="submit-cargo-btn">
                {editing ? 'Salvar Alterações' : 'Criar Cargo'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Cargos;
